import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';


const ProductDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inWishlist, setInWishlist] = useState(false); 
  const [cartSuccess, setCartSuccess] = useState(false);
  const [qty, setQty] = useState(1);
  const [enquiryLoading, setEnquiryLoading] = useState(false);
  const [enquirySent, setEnquirySent] = useState(false);
  const [showEnquiryModal, setShowEnquiryModal] = useState(false);
  const [enquiryMessage, setEnquiryMessage] = useState('');

  const API_URL = process.env.REACT_APP_API_URL;
  const BASE_URL = process.env.REACT_APP_BASE_URL || 'http://localhost:4000';

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/public/animal/${id}`);
        if (!res.ok) throw new Error('Failed to fetch');
        const data = await res.json();
        setProduct(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id, API_URL]);

  if (loading) return (
    <div className="bg-background min-h-screen flex items-center justify-center">
      <div className="text-center space-y-4">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
        <p className="text-on-surface-variant font-medium">Loading...</p>
      </div>
    </div>
  );

  if (error || !product) return (
    <div className="bg-background min-h-screen flex items-center justify-center">
      <div className="text-center space-y-4">
        <p className="text-on-surface font-bold text-xl">Product not found</p>
        <button onClick={() => navigate('/search')}
          className="px-6 py-3 bg-primary text-white rounded-full font-bold">
          Back to Marketplace
        </button>
      </div>
    </div>
  );

  const openEnquiryModal = () => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    if (!userData.token) { navigate('/login'); return; }
    setEnquiryMessage(`Hi, I'm interested in ${product.name}. Is it still available?`);
    setShowEnquiryModal(true);
  };

  const handleSendEnquiry = async () => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    if (!userData.token) { navigate('/login'); return; }
    if (!enquiryMessage.trim()) return;

    setEnquiryLoading(true);
    try {
      await fetch(`${API_URL}/api/customer/inquiries`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userData.token}`,
        },
        body: JSON.stringify({
          type: 'animal',
          animal_id: product.id,
          seller_id: product.seller_id,
          message: enquiryMessage.trim(),
        }),
      });
      setShowEnquiryModal(false);
      setEnquiryMessage('');
      setEnquirySent(true);
      setTimeout(() => setEnquirySent(false), 3000);
    } catch (err) {
      console.error('Enquiry failed:', err);
    } finally {
      setEnquiryLoading(false);
    }
  };

  // Map API fields
  const images = (product.images && product.images.length > 0)
    ? product.images
    : product.image_url
      ? [product.image_url]
      : ['/assets/placeholder.png'];

  const viewerId = JSON.parse(localStorage.getItem('user') || '{}').id;
  const isOwnListing = viewerId != null && viewerId === product.seller_id;

  const handleAddToCart = async (buyNow = false) => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    if (!userData.token) {
      // Save intended product to sessionStorage so after login we can add it
      sessionStorage.setItem('pending_cart_item', JSON.stringify({ id: product.id }));
      navigate('/login');
      return;
    }

    try {
      // Add to cart via API directly
      await fetch(`${API_URL}/api/customer/cart/sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userData.token}`,
        },
        body: JSON.stringify({
          items: [{
            id:        product.id,
            name:      product.name,
            price:     Number(product.price_inr),
            image:     product.image_url || null,
            category:  product.category,
            seller_id: product.seller_id,
            quantity:  qty,
            stock:     1,
          }]
        }),
      });
    } catch (err) {
      console.error('Cart sync failed:', err);
    }

    if (buyNow) navigate('/customer#cart');
    else {
      setCartSuccess(true);
      setTimeout(() => setCartSuccess(false), 3000);
    }
  };

  return (
    <div className="bg-background text-on-surface font-body">
      <Navbar variant="default" />

      <main className="pt-28 pb-20 max-w-screen-2xl mx-auto px-6 md:px-12">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 mb-8 text-sm font-medium text-on-secondary-container">
          <a onClick={() => navigate('/')} className="hover:text-primary cursor-pointer">
            Home
          </a>
          <span className="material-symbols-outlined text-xs">chevron_right</span>
          <a onClick={() => navigate('/search')} className="hover:text-primary cursor-pointer">
            Marketplace
          </a>
          <span className="material-symbols-outlined text-xs">chevron_right</span>
          <span className="text-on-surface">{product.name}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Left: Image Gallery */}
          <div className="lg:col-span-8 space-y-8">
            {/* Main Image */}
            <div className="relative group overflow-hidden rounded-2xl bg-surface-container h-[500px]">
              <img
                src={images[currentImageIndex]}
                alt={product.name}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
              
              {/* Image Counter */}
              <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-md px-4 py-2 rounded-full text-sm font-bold text-on-surface">
                {currentImageIndex + 1} / {images.length}
              </div>

              <div className={`absolute top-4 left-4 px-3 py-1.5 rounded-full text-sm font-bold ${
                product.quantity === 0 || product.status === 'sold'
                  ? 'bg-orange-500 text-white'
                  : 'bg-black/60 text-white'
              }`}>
                {product.quantity === 0 || product.status === 'sold' ? 'Sold Out' : `${product.quantity} left`}
              </div>

              {/* Navigation */}
              {images.length > 1 && (
                <>
                  <button
                    onClick={() => setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length)}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full transition-all"
                  >
                    <span className="material-symbols-outlined">chevron_left</span>
                  </button>
                  <button
                    onClick={() => setCurrentImageIndex((prev) => (prev + 1) % images.length)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full transition-all"
                  >
                    <span className="material-symbols-outlined">chevron_right</span>
                  </button>
                </>
              )}
            </div>

            {/* Thumbnail Gallery */}
            <div className="flex gap-4 overflow-x-auto pb-2">
              {images.map((image, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentImageIndex(idx)}
                  className={`flex-shrink-0 w-24 h-24 rounded-lg overflow-hidden border-2 transition-all ${
                    currentImageIndex === idx ? 'border-primary' : 'border-outline-variant/30'
                  }`}
                >
                  <img src={image} alt={`${product.name} ${idx + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>

            {/* Product Info */}
            <section className="space-y-8">
              <div className="border-b border-outline-variant pb-8">
                <h1 className="text-5xl font-extrabold tracking-tight text-on-surface mb-4">{product.name}</h1>
                <p className="text-xl text-on-surface-variant font-light max-w-2xl leading-relaxed">{product.description}</p>
              </div>

              {/* Specs Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-surface-container-low p-6 rounded-xl border border-transparent hover:border-primary-container transition-all">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-on-secondary-container font-bold">Breed Type</span>
                  <p className="text-2xl font-black mt-1 text-primary">{product.breed}</p>
                </div>
                <div className="bg-surface-container-low p-6 rounded-xl border border-transparent hover:border-primary-container transition-all">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-on-secondary-container font-bold">Weight</span>
                  <p className="text-2xl font-black mt-1 text-primary">{product.weight_kg} kg</p>
                </div>
                <div className="bg-surface-container-low p-6 rounded-xl border border-transparent hover:border-primary-container transition-all">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-on-secondary-container font-bold">Age</span>
                  <p className="text-2xl font-black mt-1 text-primary">{product.age_months} Months</p>
                </div>
                <div className="bg-surface-container-low p-6 rounded-xl border border-transparent hover:border-primary-container transition-all">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-on-secondary-container font-bold">Teeth</span>
                  <p className="text-2xl font-black mt-1 text-primary">{product.teeth_count}</p>
                </div>
              </div>

              {/* Detailed Description */}
              <div className="space-y-6">
                <h2 className="text-2xl font-bold">Animal Pedigree & Details</h2>
                <div className="space-y-4 text-on-surface-variant leading-relaxed">
                  <p>{product.pedigree}</p>
                  <div>
                    <strong className="text-on-surface">Temperament:</strong> {product.temperament}
                  </div>
                  <div>
                    <strong className="text-on-surface">Health Status:</strong> {product.health_status}
                  </div>
                  <div>
                    <strong className="text-on-surface">Usage:</strong> {product.usage}
                  </div>
                </div>
              </div>
            </section>
          </div>

          {/* Right: Pricing & Seller */}
          <div className="lg:col-span-4">
            <div className="sticky top-28 space-y-6">
              {/* Pricing Card */}
              <div className="bg-surface-container-lowest editorial-shadow rounded-2xl p-8 border border-outline-variant/20">
                <div className="flex justify-between items-baseline mb-6">
                  <span className="text-on-secondary-container font-medium">Price</span>
                  <span className="text-4xl font-black text-primary tracking-tight">₹{Number(product.price_inr).toLocaleString('en-IN')}</span>
                </div>
                {/* Pricing Card buttons */}
                <div className="space-y-3">
                  {product.quantity === 0 || product.status === 'sold' ? (
                    <>
                      {/* Sold Out Banner */}
                      <div className="bg-orange-50 border border-orange-200 rounded-xl px-4 py-3 flex items-center gap-2">
                        <span className="material-symbols-outlined text-orange-500">info</span>
                        <div>
                          <p className="text-sm font-bold text-orange-800">This animal has been sold</p>
                          <p className="text-xs text-orange-600">You can inquire about similar animals</p>
                        </div>
                      </div>

                      {/* Disabled Add to Cart */}
                      <button disabled
                        className="w-full bg-surface-container text-outline font-bold py-4 rounded-xl flex items-center justify-center gap-3 cursor-not-allowed">
                        <span className="material-symbols-outlined">shopping_cart_off</span>
                        Sold Out
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-sm font-semibold text-secondary">Qty:</span>
                        <div className="flex items-center gap-2 bg-surface-container rounded-xl px-3 py-1.5">
                          <button onClick={() => setQty(q => Math.max(1, q - 1))}
                            className="w-7 h-7 flex items-center justify-center font-bold text-secondary">−</button>
                          <span className="w-8 text-center font-black text-sm">{qty}</span>
                          <button onClick={() => setQty(q => Math.min(product.quantity || 99, q + 1))}
                            className="w-7 h-7 flex items-center justify-center font-bold text-secondary">+</button>
                        </div>
                        {qty >= product.quantity && (
                          <p className="text-xs text-orange-600 font-semibold">
                            Only {product.quantity} available
                          </p>
                        )}
                        {qty > 1 && (
                          <span className="text-sm font-black text-primary">
                            = ₹{(Number(product.price_inr) * qty).toLocaleString('en-IN')}
                          </span>
                        )}
                      </div>

                      {/* <button
                        onClick={() => handleAddToCart(false)}
                        className="w-full bg-primary text-on-primary font-bold py-4 rounded-xl flex items-center justify-center gap-3 transition-all hover:bg-primary/90 active:scale-[0.98] shadow-lg shadow-primary/20">
                        <span className="material-symbols-outlined">shopping_cart</span>
                        Add to Cart
                      </button>

                      {cartSuccess && (
                        <p className="text-center text-green-600 font-semibold text-sm">✓ Added to cart!</p>
                      )}

                      <button
                        onClick={() => handleAddToCart(true)}
                        className="w-full bg-amber-500 text-white font-bold py-4 rounded-xl flex items-center justify-center gap-3 transition-all hover:bg-amber-600 active:scale-[0.98]">
                        <span className="material-symbols-outlined">bolt</span>
                        Buy Now
                      </button> */}

                      {isOwnListing ? (
                        <div className="bg-surface-container-low border border-outline-variant/20 rounded-xl px-4 py-3 flex items-center gap-2">
                          <span className="material-symbols-outlined text-secondary">info</span>
                          <p className="text-sm font-semibold text-secondary">This is your own listing</p>
                        </div>
                      ) : (
                        <>
                          <button
                            onClick={openEnquiryModal}
                            className="w-full bg-primary text-on-primary font-bold py-4 rounded-xl flex items-center justify-center gap-3 transition-all hover:bg-primary/90 active:scale-[0.98] shadow-lg shadow-primary/20">
                            <span className="material-symbols-outlined">forum</span>
                            Enquiry Now
                          </button>

                          {enquirySent && (
                            <p className="text-center text-emerald-600 font-semibold text-sm">
                              ✓ Enquiry sent! Track it in "My Inquiries"
                            </p>
                          )}
                        </>
                      )}
                    </>
                  )}

                  <button
                    onClick={() => {
                      const userData = JSON.parse(localStorage.getItem('user') || '{}');
                      if (!userData.token) { navigate('/login'); return; }
                      fetch(`${API_URL}/api/customer/wishlist/${product.id}`, {
                        method: 'POST',
                        headers: { Authorization: `Bearer ${userData.token}` }
                      }).then(r => r.json()).then(d => {
                        setInWishlist(true);
                      });
                    }}
                    className="w-full border-2 border-primary text-primary font-bold py-4 rounded-xl flex items-center justify-center gap-3 transition-all hover:bg-primary/5 active:scale-[0.98]">
                    <span className="material-symbols-outlined"
                      style={inWishlist ? { fontVariationSettings: "'FILL' 1", color: 'red' } : {}}>
                      favorite
                    </span>
                    {inWishlist ? 'Saved to Wishlist' : 'Save to Wishlist'}
                  </button>

                  {!isOwnListing && (
                    <button
                      onClick={() => {
                        const msg = `Hi, I'm interested in ${product.name} priced at ₹${Number(product.price_inr).toLocaleString('en-IN')}`;
                        window.open(`https://wa.me/${product.seller_phone}?text=${encodeURIComponent(msg)}`, '_blank');
                      }}
                      className="w-full bg-[#25D366] text-white font-bold py-4 rounded-xl flex items-center justify-center gap-3 transition-all hover:bg-[#20bd5a] active:scale-[0.98]">
                      <svg className="w-6 h-6 fill-current" viewBox="0 0 24 24">
                        <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.316 1.592 5.43 0 9.856-4.426 9.858-9.855.002-5.43-4.425-9.856-9.853-9.856-5.417 0-9.856 4.425-9.859 9.854-.001 2.09.547 3.567 1.461 5.145l-1.08 3.945 4.107-1.08z" />
                      </svg>
                      Chat on WhatsApp
                    </button>
                  )}
                </div>
                <div className="mt-6 flex items-center justify-center gap-2 text-on-secondary-container text-xs font-semibold">
                  <span className="material-symbols-outlined text-sm text-primary">verified_user</span>
                  Escrow Protection Active
                </div>
              </div>

              {/* Seller Card */}
              <div className="bg-surface-container editorial-shadow rounded-2xl p-6">
                <h3 className="text-[10px] uppercase tracking-[0.2em] text-on-secondary-container font-black mb-5">
                  Seller Information
                </h3>
                <div className="flex items-center gap-4 mb-6">
                  <img
                    src={product.seller_image ? `${BASE_URL}${product.seller_image}` : `https://ui-avatars.com/api/?name=${product.seller_name}&background=random`}
                    alt={product.seller_name}
                    className="w-16 h-16 rounded-full object-cover border-2 border-white ring-1 ring-black/5"
                  />
                  <div>
                    <div className="flex items-center gap-1">
                      <h4 className="font-bold text-lg">{product.seller_name}</h4>
                      <span className="material-symbols-outlined text-primary text-lg">verified</span>
                    </div>
                    <p className="text-sm text-on-surface-variant flex items-center gap-1">
                      <span className="material-symbols-outlined text-sm">location_on</span>
                      {product.seller_city}, {product.seller_state}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-6 text-center">
                  <div className="bg-surface-container-high/50 py-3 rounded-xl">
                    <p className="text-[10px] uppercase text-on-surface-variant font-bold">Member Since</p>
                    <p className="font-black text-on-surface">{new Date(product.seller_member_since).getFullYear()}</p>
                  </div>
                  <div className="bg-surface-container-high/50 py-3 rounded-xl">
                    <p className="text-[10px] uppercase text-on-surface-variant font-bold">Sales</p>
                    <p className="font-black text-on-surface">{product.seller_total_sales}</p>
                  </div>
                </div>
                <button className="w-full text-primary font-bold py-3 border-2 border-primary/10 rounded-xl hover:bg-primary/5 transition-colors" onClick={() => navigate(`/breeder/${product.seller_public_id || product.seller_id}`)}>
                  View Breeder Profile
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Related Listings */}
        <section className="mt-24">
          <div className="flex justify-between items-end mb-10">
            <div className="cursor-pointer hover:opacity-70 transition-opacity" onClick={() => navigate(`/breeder/${product.seller_public_id || product.seller_id}`)}>
              <h2 className="text-3xl font-extrabold tracking-tight">More from {product.seller_name}</h2>
              <p className="text-on-surface-variant mt-1">Premium livestock currently available from this breeder.</p>
            </div>
            <a onClick={() => navigate('/search')} className="text-primary font-bold flex items-center gap-2 hover:gap-3 transition-all cursor-pointer group">
              See All Listings <span className="material-symbols-outlined transition-transform group-hover:translate-x-1">arrow_forward</span>
            </a>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {(product.related_listings || []).map((item) => (
              <div key={item.id} className="group cursor-pointer" onClick={() => navigate(`/product/${item.public_id || item.id}`)}>
                <div className="relative overflow-hidden rounded-2xl h-64 mb-4 shadow-sm">
                  <img
                    src={
                        item.image_url
                          ? item.image_url.startsWith('http')
                            ? item.image_url
                            : `${BASE_URL}${item.image_url}`
                          : '/assets/placeholder.png'
                      }
                    alt={item.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  {item.badge && (
                    <div className={`absolute top-3 right-3 ${item.badge === 'Price Drop' ? 'bg-tertiary-container text-white' : 'bg-white/95 text-primary'} text-[10px] font-black uppercase tracking-wider px-3 py-1.5 rounded-full shadow-sm`}>
                      {item.badge}
                    </div>
                  )}
                </div>
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-bold text-lg group-hover:text-primary transition-colors">{item.name}</h3>
                    <p className="text-on-surface-variant text-sm">{item.location_city}, {item.location_state}</p>
                  </div>
                  <p className="font-black text-lg text-primary">₹{Number(item.price_inr).toLocaleString('en-IN')}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>

      {/* Footer */}
      <Footer variant="default" />
            {/* Enquiry Modal */}
      {showEnquiryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setShowEnquiryModal(false)}
          />
          <div className="relative bg-white rounded-2xl w-full max-w-md p-6 space-y-4 shadow-xl">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-black text-on-surface">Send Enquiry</h3>
              <button
                onClick={() => setShowEnquiryModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-surface-container-high">
                <span className="material-symbols-outlined text-base">close</span>
              </button>
            </div>

            <div className="flex items-center gap-3 bg-surface-container-low rounded-xl p-3">
              <img
                src={images[0]}
                alt={product.name}
                className="w-12 h-12 rounded-lg object-cover"
              />
              <div>
                <p className="font-bold text-sm text-on-surface">{product.name}</p>
                <p className="text-xs text-secondary">
                  ₹{Number(product.price_inr).toLocaleString('en-IN')} · To: {product.seller_name}
                </p>
              </div>
            </div>

            <div>
              <label className="text-xs font-bold text-secondary uppercase tracking-wider mb-2 block">
                Your message
              </label>
              <textarea
                value={enquiryMessage}
                onChange={(e) => setEnquiryMessage(e.target.value)}
                rows={5}
                placeholder="Ask about health, delivery, price negotiation, weight verification, etc."
                className="w-full bg-surface-container-low border border-outline-variant/20 rounded-xl px-4 py-3 text-sm text-on-surface outline-none focus:ring-2 focus:ring-primary resize-none"
              />
            </div>

            <button
              onClick={handleSendEnquiry}
              disabled={enquiryLoading || !enquiryMessage.trim()}
              className="w-full bg-primary text-on-primary font-bold py-4 rounded-xl flex items-center justify-center gap-3 transition-all hover:bg-primary/90 active:scale-[0.98] shadow-lg shadow-primary/20 disabled:opacity-50">
              <span className="material-symbols-outlined">send</span>
              {enquiryLoading ? 'Sending...' : 'Send Enquiry'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetails;
